import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react"

import DeploymentPage from "@/pages/deployment"
import { C } from "@/theme/newDesignTokens"
import { checkBackendHealth, HEALTH_POLL_INTERVAL_MS } from "@/utils/checkBackendHealth"

export const MIN_DEPLOYMENT_PAGE_DISPLAY_MS = 3000
// Avoid flashing the deployment UI on a single transient /health failure during rollouts.
export const UNAVAILABLE_SHOW_DELAY_MS = 500

type GateStatus = "checking" | "unavailable" | "ready"

interface DeploymentGateProps {
    children: ReactNode
    onBootstrap: () => Promise<void>
    healthPollIntervalMs?: number
}

const checkingPlaceholderStyle: CSSProperties = {
    minHeight: "100vh",
    backgroundColor: C.pageBg,
}

export function DeploymentGate({
    children,
    onBootstrap,
    healthPollIntervalMs = HEALTH_POLL_INTERVAL_MS,
}: DeploymentGateProps) {
    const [status, setStatus] = useState<GateStatus>("checking")
    const bootstrapStarted = useRef(false)
    const deploymentPageShownAtRef = useRef<number | null>(null)
    const consecutiveFailuresRef = useRef(0)

    useEffect(() => {
        if (status === "ready") {
            return
        }

        let cancelled = false
        let unavailableShowTimerId: number | undefined

        const markUnavailable = () => {
            if (deploymentPageShownAtRef.current === null) {
                deploymentPageShownAtRef.current = Date.now()
            }
            setStatus("unavailable")
        }

        const scheduleUnavailableUi = () => {
            if (unavailableShowTimerId !== undefined) {
                return
            }

            unavailableShowTimerId = window.setTimeout(() => {
                unavailableShowTimerId = undefined
                if (!cancelled && consecutiveFailuresRef.current > 0) {
                    markUnavailable()
                }
            }, UNAVAILABLE_SHOW_DELAY_MS)
        }

        const clearUnavailableUiSchedule = () => {
            if (unavailableShowTimerId !== undefined) {
                window.clearTimeout(unavailableShowTimerId)
                unavailableShowTimerId = undefined
            }
        }

        const poll = async () => {
            const isHealthy = await checkBackendHealth()

            if (cancelled || bootstrapStarted.current) {
                return
            }

            if (isHealthy) {
                consecutiveFailuresRef.current = 0
                clearUnavailableUiSchedule()

                if (bootstrapStarted.current) {
                    return
                }

                bootstrapStarted.current = true

                try {
                    await onBootstrap()
                } catch (error) {
                    bootstrapStarted.current = false
                    if (!cancelled) {
                        markUnavailable()
                    }
                    if (import.meta.env.DEV) {
                        console.error("MSAL bootstrap failed after health check:", error)
                    }
                    return
                }

                const shownAt = deploymentPageShownAtRef.current
                const remaining = shownAt ? Math.max(0, MIN_DEPLOYMENT_PAGE_DISPLAY_MS - (Date.now() - shownAt)) : 0
                if (remaining > 0) {
                    await new Promise(resolve => window.setTimeout(resolve, remaining))
                }

                if (!cancelled) {
                    setStatus("ready")
                }
                return
            }

            consecutiveFailuresRef.current += 1
            if (consecutiveFailuresRef.current >= 2) {
                markUnavailable()
                return
            }

            scheduleUnavailableUi()
        }

        void poll()

        const intervalId = window.setInterval(() => {
            void poll()
        }, healthPollIntervalMs)

        return () => {
            cancelled = true
            clearUnavailableUiSchedule()
            window.clearInterval(intervalId)
            bootstrapStarted.current = false
        }
    }, [status, onBootstrap, healthPollIntervalMs])

    if (status === "ready") {
        return <>{children}</>
    }

    if (status === "unavailable") {
        return <DeploymentPage />
    }

    return <div style={checkingPlaceholderStyle} aria-busy="true" aria-live="polite" />
}

export default DeploymentGate
